import React, { useEffect, useState } from 'react'
import { Button, Grid, Tooltip, Typography, Stack } from '@mui/material'
import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import ComposeMessage from './ComposeMessage'
import { Link } from 'react-router-dom'
import { faDownload, faReply } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DOMPurify from 'dompurify'
import DeletedMessage from './DeletedMessage'
import ArchivedMessage from './ArchivedMessage'
import Avatar from '@/Components/Elements/Avatar'
import { StyledButton } from '@/Components/Elements/StyledComponents/Buttons'
import { ReadMessageProps } from './types'

const ReadMessage: React.FC<ReadMessageProps> = ({ kedo, id, filter }) => {
    const [compose, setCompose] = useState(false)
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState<any>({})

    const getAndSetReadItem = () => {
        setLoading(true)
        kedo.api()
            .get(`${kedo.api().getMessageEndpoint()}/${id}`)
            .then((response: any) => {
                setMessage(response.data)
                setLoading(false)
            })
    }

    useEffect(() => {
        getAndSetReadItem()
    }, [id])

    const redirectToInbox = async () => {
        const apiResult = await kedo.api().getMessageEndpoint()
        window.location.href = apiResult + 's'
    }

    const renderFiles = (message: any) => {
        if (!message.files) return null
        const baseUrl = kedo.api().getBaseUrl()

        return (
            <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
                {message.files.map((fileItem: any) => (
                    <Button
                        key={fileItem.id}
                        target="_blank"
                        href={`${baseUrl}/file/${fileItem.id}/download`}
                        variant="outlined"
                        startIcon={<FontAwesomeIcon icon={faDownload} />}
                    >
                        {fileItem.name}
                    </Button>
                ))}
            </Stack>
        )
    }

    const composeMessage = () => (
        <ComposeMessage
            kedo={kedo}
            to={[
                {
                    id: message.sender_id,
                    sender_name: message.sender_name,
                },
            ]}
            title={message.title}
            dossierId={message.dossier_id}
            filter={filter}
            onCloseModal={() => setCompose(false)}
        />
    )

    if (loading) return <LoadingDefault />

    const settings = {
        ALLOWED_TAGS: [
            'b',
            'br',
            'span',
            'table',
            'ol',
            'tbody',
            'li',
            'i',
            'strong',
            'tr',
            'td',
            'thead',
            'div',
            'p',
            'h1',
            'h2',
        ],
        ALLOWED_ATTR: ['style', 'border'],
    }

    return (
        <Grid container className="mainContainer readMessage">
            <Grid size={6}>
                {compose && composeMessage()}
                <Grid container spacing={1} alignItems="flex-start">
                    <Avatar
                        name={message.sender_name}
                        key={message.sender_id}
                        kedo={kedo}
                        user={{
                            userId: message.sender_id,
                            username: message.sender_name,
                        }}
                        options={{ size: 38, showPresence: false }}
                        props={{ kedo }}
                    />
                    <Stack spacing={0.5} ml={1}>
                        <Typography
                            component={Link}
                            to={`/user/${message.sender_id}`}
                            fontWeight="bold"
                        >
                            {message.sender_name}
                        </Typography>
                        <Typography variant="body2">
                            {`${kedo.t('To')}: ${
                                message.recipients && message.recipients[0]
                                    ? message.recipients[0].recipient_name
                                    : 'No recipients'
                            }`}
                        </Typography>
                    </Stack>
                </Grid>
            </Grid>

            <Grid
                size={6}
                container
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
            >
                <DeletedMessage
                    kedo={kedo}
                    messageId={id}
                    updateMessage={redirectToInbox}
                />
                <ArchivedMessage
                    kedo={kedo}
                    messageId={id}
                    updateMessage={redirectToInbox}
                />
                <Tooltip title={kedo.t('Reply')} arrow>
                    <StyledButton
                        onClick={() => setCompose(true)}
                        sx={{ height: '38px' }}
                    >
                        <FontAwesomeIcon icon={faReply} />
                    </StyledButton>
                </Tooltip>
            </Grid>

            <Grid
                size={12}
                container
                justifyContent="flex-end"
                alignItems="center"
            >
                <Typography
                    variant="caption"
                    sx={{ ml: 2, fontWeight: 'bold' }}
                >
                    {kedo.utils().dateFormat(message.created_at)}
                </Typography>
            </Grid>

            <Grid size={12} mt={2}>
                <Typography variant="h5" fontWeight="bold">
                    {message.title}{' '}
                    {message.dossier_id && (
                        <Typography
                            component="a"
                            href={`/contentdossier/${message.dossier_id}`}
                            variant="body2"
                            sx={{ ml: 1 }}
                        >
                            {kedo.t('Dossier')} {message.dossier_id}
                        </Typography>
                    )}
                </Typography>

                {renderFiles(message)}

                <Typography
                    variant="body1"
                    mt={2}
                    component="div"
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                            message.message_text,
                            settings
                        ),
                    }}
                />
            </Grid>
        </Grid>
    )
}

export default ReadMessage
